import { useEffect, useRef } from 'react';

// Centralized mobile back-button controller.
//
// Every back-closable thing (modal, drawer, non-dashboard page) registers a
// "layer" here via useHistoryBack; useBackTrap registers the dashboard root.
// The controller owns ALL history manipulation so components never race each
// other (the bug that plagued the old per-hook push/pop model).
//
// How it works:
//   - `layers` is the ordered list of open layers (bottom→top).
//   - Above a single base sentinel we keep exactly `layers.length` anonymous
//     history "marker" entries. `reconcile()` pushes/pops markers to match, and
//     is batched to run once per commit — so a simultaneous add+remove of equal
//     count (e.g. closing the nav drawer while navigating to a page) nets to
//     ZERO history ops and cannot desync.
//   - A genuine system back pops the top marker; we close the top layer (which
//     unregisters it, leaving reconcile a no-op). No extra history.back().
//   - At the root (dashboard, no layers) a back hits the sentinel: first press
//     re-pushes it and shows a "press again to exit" toast; a second press
//     within the window exits.

const layers = [];        // ordered [{ id, close }], bottom→top
let depth = 0;            // marker entries pushed above the sentinel (kept == layers.length)
let pendingPops = 0;      // self-initiated history.back()s whose popstate must be ignored
let listening = false;
let trapEnabled = false;
let sentinelPushed = false;
let reconcileScheduled = false;

const EXIT_WINDOW_MS = 2000; // a second root-back within this window exits the app
let lastRootBackAt = 0;
let exitPromptCb = null;     // shows the "press back again to exit" toast

const pushSentinel = () => {
    try {
        window.history.pushState({ __backTrap: true }, '');
    } catch (error) {
        console.error('Error pushing back sentinel:', error);
    }
};

function updateListener() {
    const needed = layers.length > 0 || trapEnabled || pendingPops > 0;
    if (needed && !listening) {
        window.addEventListener('popstate', onPopState);
        listening = true;
    } else if (!needed && listening) {
        window.removeEventListener('popstate', onPopState);
        listening = false;
    }
}

function reconcile() {
    const desired = layers.length;
    if (depth < desired) {
        for (let i = depth; i < desired; i += 1) {
            try {
                window.history.pushState({ __backDepth: i + 1 }, '');
            } catch (error) {
                console.error('Error pushing history marker:', error);
            }
        }
        depth = desired;
    } else if (depth > desired) {
        const n = depth - desired;
        depth = desired;
        pendingPops += n;
        for (let i = 0; i < n; i += 1) {
            try {
                window.history.back();
            } catch (error) {
                pendingPops -= 1;
                console.error('Error popping history marker:', error);
            }
        }
    }
    updateListener();
}

// Batch reconciliation to run once after all of a commit's register/unregister
// changes settle, so add+remove pairs net correctly with no intermediate churn.
function scheduleReconcile() {
    if (reconcileScheduled) return;
    reconcileScheduled = true;
    queueMicrotask(() => {
        reconcileScheduled = false;
        reconcile();
    });
}

function onPopState() {
    if (pendingPops > 0) {
        pendingPops -= 1;
        updateListener();
        return;
    }

    if (layers.length > 0) {
        // Genuine back: the browser popped the top marker. Close the top layer;
        // its unregister will leave reconcile a no-op (depth already matches).
        depth = Math.max(0, depth - 1);
        layers[layers.length - 1].close();
        updateListener();
        return;
    }

    if (trapEnabled) {
        // Root back: the sentinel was popped.
        const now = Date.now();
        if (now - lastRootBackAt < EXIT_WINDOW_MS) {
            // Second back within the window → exit. Pop the app's own entry; if
            // there's nothing before it (no-op), restore the sentinel.
            lastRootBackAt = 0;
            try {
                window.history.back();
            } catch (error) {
                console.error('Error exiting on back:', error);
            }
            window.setTimeout(() => {
                const st = window.history.state;
                if (trapEnabled && layers.length === 0 && !(st && st.__backTrap)) {
                    pushSentinel();
                    updateListener();
                }
            }, 150);
        } else {
            // First back at the root → stay, re-push the sentinel, prompt to exit.
            lastRootBackAt = now;
            pushSentinel();
            if (exitPromptCb) exitPromptCb();
        }
    }
    updateListener();
}

const useHistoryBack = (isOpen, onClose, isEnabled = true, drawerId = 'default') => {
    // Keep the latest onClose in a ref so an unstable (inline) handler doesn't
    // re-run the effect below.
    const onCloseRef = useRef(onClose);
    useEffect(() => { onCloseRef.current = onClose; }, [onClose]);

    // One stable layer object per hook instance.
    const layerRef = useRef(null);
    if (!layerRef.current) {
        layerRef.current = { id: drawerId, close: () => onCloseRef.current?.() };
    }

    const active = !!(isOpen && isEnabled);

    useEffect(() => {
        if (!active) return undefined;

        const layer = layerRef.current;
        layer.id = drawerId;
        layers.push(layer);
        updateListener();
        scheduleReconcile();

        return () => {
            const idx = layers.lastIndexOf(layer);
            if (idx !== -1) layers.splice(idx, 1);
            updateListener();
            scheduleReconcile();
        };
    }, [active, drawerId]);
};

// Register the dashboard root so the system back button doesn't exit the app on
// the first press: with nothing open, the first back re-pushes the sentinel and
// prompts ("press back again to exit"); a second back within 2s exits.
export const useBackTrap = (isEnabled, onExitPrompt) => {
    const promptRef = useRef(onExitPrompt);
    useEffect(() => { promptRef.current = onExitPrompt; }, [onExitPrompt]);

    useEffect(() => {
        if (!isEnabled) return undefined;

        trapEnabled = true;
        exitPromptCb = () => promptRef.current?.();
        if (!sentinelPushed) {
            pushSentinel();
            sentinelPushed = true;
        }
        updateListener();

        return () => {
            trapEnabled = false;
            exitPromptCb = null;
            updateListener();
        };
    }, [isEnabled]);
};

export default useHistoryBack;
